/**
 * The root error boundary (Cedar removal, Stage 4.1a): @cedarjs/web 5.0.5's
 * `FatalErrorBoundary` (components/FatalErrorBoundary.js) as it was. An error
 * that reaches the root renders the fatal page; if the page itself throws, an
 * infallible fallback renders one line.
 */
import React from 'react'
import type { ReactNode } from 'react'

class InfallibleErrorBoundary extends React.Component<
  { children?: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong and we are unable to show this page.</h1>
    }
    return this.props.children
  }
}

export interface FatalErrorBoundaryProps {
  page: React.ComponentType<{ error?: Error }>
  children?: ReactNode
}

export class FatalErrorBoundary extends React.Component<
  FatalErrorBoundaryProps,
  { hasError: boolean; error?: Error }
> {
  state: { hasError: boolean; error?: Error } = {
    hasError: false,
    error: undefined,
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  render() {
    const { page: Page } = this.props
    if (this.state.hasError) {
      return (
        <InfallibleErrorBoundary>
          <Page error={this.state.error} />
        </InfallibleErrorBoundary>
      )
    }
    return this.props.children
  }
}
